import type {
  ImportJob,
  ImportJobListItem,
  ImportJobStatus,
} from "@adva/types";

export const IMPORT_STATUS_LABELS: Record<ImportJobStatus, string> = {
  pending: "Queued",
  validating: "Validating…",
  importing: "Importing…",
  staged: "Processing",
  normalizing: "Processing",
  // ADV-1108: sentence case per ADR-0019.
  awaiting_review: "Ready to review",
  partially_approved: "Completed (partial)",
  completed: "Completed",
  failed: "Failed",
  expired: "Expired",
  discarded: "Discarded",
  cancelled: "Cancelled",
};

export const ATTENTION_STATUSES: ImportJobStatus[] = [
  "awaiting_review",
  "expired",
];

export const IN_PROGRESS_STATUSES: ImportJobStatus[] = [
  "pending",
  "validating",
  "importing",
  "staged",
  "normalizing",
];

export const RECENT_STATUSES: ImportJobStatus[] = [
  "completed",
  "partially_approved",
  "failed",
  "discarded",
];

export function bucketImportJobs(jobs: ImportJobListItem[]): {
  attention: ImportJobListItem[];
  inProgress: ImportJobListItem[];
  recent: ImportJobListItem[];
} {
  const attention: ImportJobListItem[] = [];
  const inProgress: ImportJobListItem[] = [];
  const recent: ImportJobListItem[] = [];

  for (const job of jobs) {
    if (ATTENTION_STATUSES.includes(job.status)) {
      attention.push(job);
    } else if (IN_PROGRESS_STATUSES.includes(job.status)) {
      inProgress.push(job);
    } else {
      recent.push(job);
    }
  }

  return { attention, inProgress, recent };
}

export function formatEntityType(type: string): string {
  return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.floor(diffHr / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

interface NormalizationResult {
  address_corrections?: number;
  phone_normalizations?: number;
  dedup_pairs?: number;
  geocoding_failures?: number;
}

export function summarizeNormalization(
  result: Record<string, unknown> | null | undefined,
): string | null {
  if (!result) return null;
  const r = result as NormalizationResult;
  const parts: string[] = [];
  if (r.address_corrections && r.address_corrections > 0) {
    parts.push(
      `${r.address_corrections} addr fix${r.address_corrections === 1 ? "" : "es"}`,
    );
  }
  if (r.phone_normalizations && r.phone_normalizations > 0) {
    parts.push(
      `${r.phone_normalizations} phone fix${r.phone_normalizations === 1 ? "" : "es"}`,
    );
  }
  if (r.dedup_pairs && r.dedup_pairs > 0) {
    parts.push(`${r.dedup_pairs} dupe${r.dedup_pairs === 1 ? "" : "s"} found`);
  }
  if (r.geocoding_failures && r.geocoding_failures > 0) {
    parts.push(
      `${r.geocoding_failures} geocoding error${r.geocoding_failures === 1 ? "" : "s"}`,
    );
  }
  return parts.length > 0 ? parts.join(", ") : null;
}

// ─── ADV-1108: situation derivation + outcome summaries ─────────────────────

// Drop the `_external_id` / `_id` suffix on an FK column name and prettify
// to the parent entity it points at, e.g. `customer_external_id` → "customer".
export function friendlyParentEntity(field: string): string {
  return field
    .replace(/_external_id$/i, "")
    .replace(/_id$/i, "")
    .replace(/_/g, " ")
    .toLowerCase()
    .trim();
}

// Naive English pluralization — enough for the FK columns we ship today
// (customer, location, proposal, order, role). Add irregulars as needed.
// Note: order matters — `address` ends in `ss` which is both "ends with s"
// and a sibilant cluster; sibilants must be checked first to produce
// `addresses`, not the no-op `address`.
export function friendlyParentEntityPlural(field: string): string {
  const base = friendlyParentEntity(field);
  if (!base) return base;
  if (/(ss|x|z|ch|sh)$/i.test(base)) return `${base}es`;
  if (base.endsWith("s")) return base;
  return `${base}s`;
}

type ImportBannerCta =
  | { kind: "download-unmatched"; label: string; count: number }
  | { kind: "switch-tab"; label: string; tabId: "errors" | "unmatched" };

export type ImportBanner = {
  variant: "warning";
  title: string;
  body: string | null;
  cta: ImportBannerCta | null;
};

type ImportSituation = {
  /** Rows that would land if the user clicked Approve all (created + updated). */
  approvable: number;
  /** Rows that won't import. Already includes `unmatched`. */
  held: number;
  unmatched: number;
  errors: number;
  duplicatesDropped: number;
  /** Human label for the page state header — replaces the raw status enum. */
  stateHeader: string;
  /** Top-of-page banner. `null` when the situation doesn't warrant one. */
  banner: ImportBanner | null;
};

function firstUnmatchedField(job: ImportJob): string | null {
  const sample = job.unmatched_rows[0];
  return sample ? sample.field : null;
}

export function deriveImportSituation(job: ImportJob): ImportSituation {
  const approvable = job.created_count + job.updated_count;
  const held = job.skipped_count;
  const unmatched = job.unmatched_count;
  const errors = job.error_rows;
  const duplicatesDropped = job.duplicates_dropped;

  let stateHeader: string;
  let banner: ImportBanner | null = null;

  const isAwaiting = job.status === "awaiting_review";

  if (isAwaiting && approvable === 0 && unmatched > 0) {
    const fkField = firstUnmatchedField(job);
    // ADV-1113 §4: name the actual import entity ("customer record"), not the
    // bare plural ("customers"), which a dogfooder read as the people-who-buy
    // sense rather than the importable `customer` entity type.
    const parentLabel = fkField ? friendlyParentEntity(fkField) : "parent";
    stateHeader = "Blocked — needs parent records imported";
    banner = {
      variant: "warning",
      title:
        "Blocked — every row references a parent that doesn't exist in this business yet.",
      body: `Import the ${unmatched.toLocaleString()} referenced ${parentLabel} records first, then re-import this CSV.`,
      cta: {
        kind: "download-unmatched",
        label: `Download ${unmatched.toLocaleString()} unmatched rows (CSV)`,
        count: unmatched,
      },
    };
  } else if (isAwaiting && approvable === 0 && errors > 0 && unmatched === 0) {
    stateHeader = "Blocked — needs validation fixes";
    banner = {
      variant: "warning",
      title: "Blocked — every row failed validation.",
      body: "Fix the source data and re-upload.",
      cta: { kind: "switch-tab", label: "Review errors", tabId: "errors" },
    };
  } else if (isAwaiting && approvable > 0 && (unmatched > 0 || errors > 0)) {
    // Mixed state — title points at the actual blocker so the user opens
    // the right tab. "Held on missing parents" is wrong when the only
    // blocker is validation errors (codex review on PR #831).
    const heldReason =
      unmatched > 0 && errors > 0
        ? "held on missing parents or validation errors"
        : unmatched > 0
          ? "held on missing parents"
          : "held by validation errors";
    stateHeader = `${held.toLocaleString()} held / ${approvable.toLocaleString()} ready`;
    banner = {
      variant: "warning",
      title: `${approvable.toLocaleString()} rows ready to approve. ${held.toLocaleString()} ${heldReason} — see below.`,
      body: null,
      cta:
        unmatched > 0
          ? {
              kind: "switch-tab",
              label: "Review unmatched",
              tabId: "unmatched",
            }
          : { kind: "switch-tab", label: "Review errors", tabId: "errors" },
    };
  } else if (isAwaiting && approvable > 0) {
    stateHeader = "Ready to approve";
  } else {
    stateHeader = IMPORT_STATUS_LABELS[job.status];
  }

  return {
    approvable,
    held,
    unmatched,
    errors,
    duplicatesDropped,
    stateHeader,
    banner,
  };
}

type ImportCounterKey =
  | "created"
  | "updated"
  | "unmatched"
  | "dup-cleared"
  | "errors";

export interface ImportCounterCell {
  key: ImportCounterKey;
  /** Short noun shown after the value, e.g. "created". */
  label: ImportCounterKey;
  value: number;
  /** True when value is 0 — render in a muted tone so columns still line up. */
  muted: boolean;
}

// ADV-1113 §2 (Option A): always emit all five counters in a fixed order so the
// eye can line up `created` (etc.) vertically across rows. Zero values are kept
// but flagged `muted` for the caller to gray out, rather than dropped.
const COUNTER_ORDER: {
  key: ImportCounterKey;
  pick: (r: ImportJobListItem) => number;
}[] = [
  { key: "created", pick: (r) => r.created_count },
  { key: "updated", pick: (r) => r.updated_count },
  { key: "unmatched", pick: (r) => r.unmatched_count },
  { key: "dup-cleared", pick: (r) => r.duplicates_dropped },
  { key: "errors", pick: (r) => r.error_rows },
];

export function importCounterCells(
  row: ImportJobListItem,
): ImportCounterCell[] {
  return COUNTER_ORDER.map(({ key, pick }) => {
    const value = pick(row);
    return { key, label: key, value, muted: value === 0 };
  });
}

type ImportOutcomeSummary = {
  /** Plain-English one-line outcome of the import for list rows. */
  headline: string;
  /** Fixed-order, always-five counter cells (ADV-1113 §2 Option A). */
  counters: ImportCounterCell[];
};

export function summarizeImportOutcome(
  row: ImportJobListItem,
): ImportOutcomeSummary {
  const approvable = row.created_count + row.updated_count;
  const held = row.skipped_count;

  let headline: string;
  switch (row.status) {
    case "awaiting_review": {
      if (row.unmatched_count > 0 && approvable === 0) {
        headline = `${row.unmatched_count.toLocaleString()} rows held — parents not imported yet`;
      } else if (approvable > 0 && held > 0) {
        headline = `${approvable.toLocaleString()} ready to approve, ${held.toLocaleString()} held`;
      } else if (approvable > 0) {
        headline = `${approvable.toLocaleString()} ready to approve`;
      } else if (row.error_rows > 0) {
        headline = `${row.error_rows.toLocaleString()} rows failed validation`;
      } else {
        headline = IMPORT_STATUS_LABELS[row.status];
      }
      break;
    }
    case "completed":
    case "partially_approved": {
      headline =
        approvable > 0
          ? `${approvable.toLocaleString()} imported`
          : IMPORT_STATUS_LABELS[row.status];
      break;
    }
    default:
      headline = IMPORT_STATUS_LABELS[row.status];
  }

  // ADV-1017: counts moved to discrete table columns. `NeedsAttentionRow`
  // calls `importCounterCells` directly; this summary now emits headline
  // only.
  return { headline, counters: [] };
}

export type ImportJobSeverity = "success" | "warning" | "danger";

// ADV-1017: bucket a terminal-status job by error rate.
// - success: 0 errors
// - warning: 1..49% error rate
// - danger:  >=50% error rate
// Denominator falls back to total_rows when processed_rows is 0
// (e.g. `failed` jobs that never started).
export function importJobSeverity(row: ImportJobListItem): ImportJobSeverity {
  // ADV-1017: a terminal `failed` job is always danger. The service can record
  // status "failed" with an aggregate sentinel error (e.g. external-ID
  // resolution failures, watchdog recovery) without incrementing error_rows —
  // the zero-error shortcut below would otherwise render those as a green
  // "success" dot despite the Failed status.
  if (row.status === "failed") return "danger";
  if (row.error_rows <= 0) return "success";
  const denominator =
    row.processed_rows > 0 ? row.processed_rows : row.total_rows;
  if (denominator <= 0) return "success";
  const rate = row.error_rows / denominator;
  return rate >= 0.5 ? "danger" : "warning";
}
